import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Home, FileText } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import InactivityTimer from '../components/InactivityTimer';

const DepositPaymentSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [collectionData, setCollectionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCollectionData = async () => {
            try {
                const orderId = searchParams.get('order_id');
                if (!orderId) {
                    setError('Order ID not found');
                    setLoading(false);
                    return;
                }

                const response = await axiosInstance.get(`/api/patient/get-collection-data/${orderId}`);
                
                if (response.data.success) {
                    setCollectionData(response.data.data);
                    
                    // Only create receipt if himsReceiptId is not already set (first time load)
                    if (!response.data.data.himsReceiptId) {
                        await createReceipt(response.data.data);
                    }
                } else {
                    setError('Failed to fetch collection data');
                }
            } catch (err) {
                console.error('Error fetching collection data:', err);
                setError(err.response?.data?.error || 'Failed to fetch collection data');
            } finally {
                setLoading(false);
            }
        };

        fetchCollectionData();
    }, [searchParams]);

    const createReceipt = async (collectionData) => {
        try {
            const token = localStorage.getItem('token');
            const departmentId = localStorage.getItem('departmentId');

            if (!token) {
                console.error('Token not found');
                return;
            }

            const patientMrno = collectionData.patientMrno;
            const receiptAmount = parseFloat(collectionData.amount_received) || 0;
            const referenceId = collectionData.receiptNumber || '123';

            // Fetch patient details to get patientId
            const patientResponse = await fetch(
                `${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(patientMrno)}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!patientResponse.ok) {
                console.error('Failed to fetch patient details');
                return;
            }

            const patientData = await patientResponse.json();
            console.log('Patient details:', patientData);

            // Extract patientId from response
            const patientId = patientData?.data?.[0]?.patientId || patientData?.patientId || patientData?.id;

            if (!patientId) {
                console.error('Patient ID not found in response');
                return;
            }

            console.log('Patient ID found:', patientId);

            // Now create receipt only after patient API responds
            const apiUrl = `${process.env.REACT_APP_KIOSK_OPS_URL}/receipt/createNewReceipt?paymentsubtype=RAZORPAY&srcDocId=9&referenceId=${referenceId}&departmentName=ACCOUNTS`;

            const requestBody = {
                receiptAmount: receiptAmount,
                depositPatient: {
                    patientId: parseInt(patientId)
                },
                billingSite: {
                    siteId: localStorage.getItem('site_id') ? parseInt(localStorage.getItem('site_id')) : 1,
                    siteCode: localStorage.getItem('site_code') || 'DEFAULT_CODE'
                },
                createdBy: 2
            };

            console.log('Creating receipt with:', requestBody);

            const receiptResponse = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            if (receiptResponse.ok) {
                const receiptData = await receiptResponse.json();
                console.log('Receipt created successfully:', receiptData);

                // Update collection with HIMS receipt ID
                if (receiptData?.receiptId) {
                    await updateHimsReceiptId(collectionData, receiptData.receiptId);
                    // Update local state with new himsReceiptId
                    setCollectionData(prev => ({
                        ...prev,
                        himsReceiptId: receiptData.receiptId
                    }));
                }
            } else {
                const errorData = await receiptResponse.text();
                console.error('Failed to create receipt:', errorData);
            }
        } catch (error) {
            console.error('Error creating receipt:', error);
        }
    };

    const updateHimsReceiptId = async (collectionData, himsReceiptId) => {
        try {
            const response = await axiosInstance.put('/api/patient/update-collection-hims-receipt', {
                collectionId: collectionData.collectionId,
                himsReceiptId: himsReceiptId
            });

            if (response.data.success) {
                console.log('HIMS Receipt ID updated successfully');
            }
        } catch (error) {
            console.error('Error updating HIMS receipt ID:', error);
        }
    };

    const handleGoHome = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading collection details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={handleGoHome}
                        className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition duration-200"
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
                    {/* Success Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                            <CheckCircle className="w-16 h-16 text-green-600" />
                        </div>
                    </div>

                    {/* Success Message */}
                    <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
                        Payment Successful!
                    </h1>
                    <p className="text-center text-gray-600 mb-8">
                        Your deposit payment has been processed successfully
                    </p>

                    {/* Collection Details */}
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <FileText className="w-5 h-5 mr-2" />
                            Collection Details
                        </h2>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">Receipt Number:</span>
                                <span className="font-semibold text-gray-800">{collectionData?.receiptNumber || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">HIMS Receipt ID:</span>
                                <span className="font-semibold text-gray-800">{collectionData?.himsReceiptId || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">Patient MRNO:</span>
                                <span className="font-semibold text-gray-800">{collectionData?.patientMrno}</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">Patient Name:</span>
                                <span className="font-semibold text-gray-800">{collectionData?.patientName || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">Collection Type:</span>
                                <span className="font-semibold text-gray-800">{collectionData?.collectionType}</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">Amount Paid:</span>
                                <span className="font-bold text-green-600 text-xl">
                                    ₹{collectionData?.amount_received ? parseFloat(collectionData.amount_received).toFixed(2) : '0.00'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">Payment ID:</span>
                                <span className="font-mono text-sm text-gray-800">{collectionData?.razorpayPaymentId}</span>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                                <span className="text-gray-600">Order ID:</span>
                                <span className="font-mono text-sm text-gray-800">{collectionData?.razorpayOrderId}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">Transaction Date:</span>
                                <span className="font-semibold text-gray-800">
                                    {collectionData?.transactionDate 
                                        ? new Date(collectionData.transactionDate).toLocaleString('en-IN', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })
                                        : 'N/A'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center">
                        <button
                            onClick={handleGoHome}
                            className="flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-200 shadow-lg"
                        >
                            <Home className="w-5 h-5" />
                            Go to Home
                        </button>
                    </div>

                    {/* Additional Info */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800 text-center">
                            <strong>Note:</strong> Please save this receipt for your records. You can also download it for future reference.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DepositPaymentSuccess;
